import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  CalendarDays, Users, Trophy, CheckCircle2, Loader2, Crown, Heart, Swords, Medal, Send,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Spinner, EmptyState } from "@/components/Bits";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";

const MEDAL = ["🥇", "🥈", "🥉"];

export default function Arena() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [events, setEvents] = useState(null);
  const [board, setBoard] = useState([]);
  const [openId, setOpenId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [myGames, setMyGames] = useState([]);
  const [pickGame, setPickGame] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get("/arena/events").then((r) => setEvents(r.data)).catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
    api.get("/arena/leaderboard").then((r) => setBoard(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const openEvent = async (id) => {
    setOpenId(id); setDetail(null); setPickGame("");
    try {
      const { data } = await api.get(`/arena/events/${id}`);
      setDetail(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    if (myGames.length === 0) {
      api.get("/games").then((r) => setMyGames((r.data || []).filter((g) => g.status === "published"))).catch(() => {});
    }
  };

  const refreshDetail = async (id) => {
    const { data } = await api.get(`/arena/events/${id}`); setDetail(data); load();
  };

  const register = async (id) => {
    try { const { data } = await api.post(`/arena/events/${id}/register`); toast.success(data.message); openId === id ? refreshDetail(id) : load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const submit = async () => {
    if (!pickGame) { toast.error("Pick one of your published games to enter."); return; }
    setBusy(true);
    try { const { data } = await api.post(`/arena/events/${openId}/submit`, { game_id: pickGame }); toast.success(data.message); setPickGame(""); refreshDetail(openId); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const vote = async (submissionId) => {
    try { const { data } = await api.post(`/arena/submissions/${submissionId}/vote`); toast.success(data.message); refreshDetail(openId); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const closeEvent = async (id) => {
    try { const { data } = await api.post(`/arena/events/${id}/close`); toast.success(data.message); refreshDetail(id); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (!events) return <Spinner />;
  const isAdmin = user?.role === "admin" || user?.role === "superadmin";

  return (
    <div className="p-5 lg:p-8 max-w-6xl mx-auto" data-testid="arena-page">
      <PageHeader title="GameView Arena" icon={Swords} subtitle="Game jams, weekly challenges and tournaments. Enter your games, vote and climb the leaderboard." />

      {events.length === 0 ? (
        <EmptyState emoji="🏟️" title="No events yet" text="New jams and challenges drop here regularly — check back soon!" />
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {events.map((e, i) => (
            <div key={e.id} className={`rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-5 card-hover animate-rise delay-${(i % 5) + 1} flex flex-col`} data-testid={`event-card-${e.id}`}>
              <div className="flex items-start justify-between">
                <div className="text-4xl">{e.icon}</div>
                <Badge className={e.status === "live" ? "bg-emerald-500" : e.status === "ended" ? "bg-[hsl(var(--muted-foreground))]" : ""} variant={e.status === "upcoming" ? "secondary" : "default"}>
                  {e.status === "live" ? "LIVE" : e.status === "ended" ? "ENDED" : "UPCOMING"}
                </Badge>
              </div>
              <h3 className="font-display text-lg font-bold mt-3">{e.title}</h3>
              <div className="text-xs font-semibold text-[hsl(var(--primary))] mb-2">{e.type} · {e.theme}</div>
              <p className="text-sm text-[hsl(var(--muted-foreground))] mb-3 line-clamp-2">{e.description}</p>
              <div className="flex items-center gap-3 text-xs text-[hsl(var(--muted-foreground))] mb-4 flex-wrap">
                <span className="flex items-center gap-1"><Users className="w-3.5 h-3.5" /> {e.participants}</span>
                <span className="flex items-center gap-1"><Send className="w-3.5 h-3.5" /> {e.submission_count} entries</span>
                <span className="flex items-center gap-1"><Trophy className="w-3.5 h-3.5" /> {e.prize}</span>
              </div>
              {e.winner && (
                <div className="mb-3 text-xs flex items-center gap-1.5 rounded-lg bg-amber-500/10 text-amber-600 px-2.5 py-1.5 font-semibold" data-testid={`event-winner-${e.id}`}>
                  <Crown className="w-3.5 h-3.5" /> Winner: {e.winner.user_name}
                </div>
              )}
              <div className="mt-auto flex gap-2">
                <Button onClick={() => openEvent(e.id)} className="rounded-full flex-1 bg-gradient-to-r from-[#1f6feb] to-[#3b9eff]" data-testid={`event-view-${e.id}`}>View & enter</Button>
                {!e.registered && e.status !== "ended" && (
                  <Button onClick={() => register(e.id)} variant="outline" className="rounded-full" data-testid={`event-register-${e.id}`}>Join</Button>
                )}
                {e.registered && <Button variant="outline" disabled className="rounded-full"><CheckCircle2 className="w-4 h-4 text-emerald-500" /></Button>}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Global creators leaderboard */}
      <div className="mt-10" data-testid="arena-leaderboard">
        <h2 className="font-display text-xl font-bold mb-3 flex items-center gap-2"><Trophy className="w-5 h-5 text-amber-500" /> Top Arena creators</h2>
        {board.length === 0 ? (
          <p className="text-sm text-[hsl(var(--muted-foreground))]">No entries yet — be the first to enter an event and earn votes!</p>
        ) : (
          <div className="rounded-2xl border border-[hsl(var(--border))] overflow-hidden">
            {board.map((r) => (
              <div key={r.user_id} className={`flex items-center gap-3 px-4 py-3 ${r.rank % 2 ? "" : "bg-[hsl(var(--secondary))]/30"}`} data-testid={`arena-leader-${r.rank}`}>
                <span className="w-7 text-center font-bold">{MEDAL[r.rank - 1] || r.rank}</span>
                <div className="flex-1 min-w-0"><div className="font-semibold truncate">{r.name}</div><div className="text-xs text-[hsl(var(--muted-foreground))]">{r.entries} entries{r.wins > 0 ? ` · ${r.wins} 🏆` : ""}</div></div>
                <span className="flex items-center gap-1 font-bold text-sm"><Heart className="w-3.5 h-3.5 text-rose-500 fill-current" /> {r.votes}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Event detail */}
      <Dialog open={!!openId} onOpenChange={(o) => { if (!o) { setOpenId(null); setDetail(null); } }}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="arena-detail-dialog">
          {!detail ? (
            <>
              <DialogHeader className="sr-only"><DialogTitle>Loading event</DialogTitle></DialogHeader>
              <div className="py-12 grid place-items-center"><Loader2 className="w-6 h-6 animate-spin text-[hsl(var(--primary))]" /></div>
            </>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 font-display"><span className="text-2xl">{detail.event.icon}</span> {detail.event.title}</DialogTitle>
                <DialogDescription>{detail.event.type} · {detail.event.theme} · 🏆 {detail.event.prize}</DialogDescription>
              </DialogHeader>

              <p className="text-sm text-[hsl(var(--muted-foreground))]">{detail.event.description}</p>

              {detail.event.winner && (
                <div className="rounded-xl bg-amber-500/10 border border-amber-500/30 p-3 flex items-center gap-2 text-sm font-semibold text-amber-600" data-testid="arena-detail-winner">
                  <Crown className="w-4 h-4" /> Winner: {detail.event.winner.user_name} — “{detail.event.winner.title}” ({detail.event.winner.votes} votes)
                </div>
              )}

              {/* Enter your game */}
              {detail.event.status !== "ended" && (
                <div className="rounded-xl border border-[hsl(var(--border))] p-3.5" data-testid="arena-submit-card">
                  <div className="font-semibold text-sm mb-2">{detail.my_submission ? "Your entry" : "Enter one of your published games"}</div>
                  {detail.my_submission ? (
                    <div className="flex items-center gap-2 text-sm">
                      <span className="text-xl">{detail.my_submission.cover_emoji}</span>
                      <span className="font-semibold flex-1 truncate">{detail.my_submission.title}</span>
                      <Badge variant="secondary">{detail.my_submission.votes} votes</Badge>
                    </div>
                  ) : null}
                  <div className="flex flex-wrap gap-2 mt-2">
                    <Select value={pickGame} onValueChange={setPickGame}>
                      <SelectTrigger className="flex-1 min-w-[180px]" data-testid="arena-submit-select"><SelectValue placeholder={myGames.length ? "Pick a published game…" : "Publish a game first"} /></SelectTrigger>
                      <SelectContent>
                        {myGames.map((g) => <SelectItem key={g.id} value={g.id}>{g.cover_emoji} {g.title}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <Button onClick={submit} disabled={busy || !pickGame} className="rounded-full" data-testid="arena-submit-confirm">
                      {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} {detail.my_submission ? "Change entry" : "Enter"}
                    </Button>
                  </div>
                  {myGames.length === 0 && <p className="text-xs text-[hsl(var(--muted-foreground))] mt-2">You need a published game to enter. <button className="text-[hsl(var(--primary))] underline" onClick={() => navigate("/app/games")}>Go to My Games</button></p>}
                </div>
              )}

              {/* Leaderboard */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-display font-bold flex items-center gap-2"><Medal className="w-4 h-4 text-amber-500" /> Leaderboard ({detail.submissions.length})</h3>
                  {isAdmin && detail.event.status !== "ended" && (
                    <Button size="sm" variant="outline" className="rounded-full" onClick={() => closeEvent(detail.event.id)} data-testid="arena-close-event">Close & crown winner</Button>
                  )}
                </div>
                {detail.submissions.length === 0 ? (
                  <p className="text-sm text-[hsl(var(--muted-foreground))]">No entries yet. Be the first to enter!</p>
                ) : (
                  <div className="space-y-2">
                    {detail.submissions.map((s) => (
                      <div key={s.id} className={`flex items-center gap-3 p-3 rounded-xl border ${s.mine ? "border-[hsl(var(--primary))] bg-[hsl(var(--primary))]/5" : "border-[hsl(var(--border))]"}`} data-testid={`arena-submission-${s.id}`}>
                        <span className="w-7 text-center font-bold shrink-0">{MEDAL[s.rank - 1] || s.rank}</span>
                        <span className="text-2xl shrink-0">{s.cover_emoji}</span>
                        <div className="flex-1 min-w-0">
                          <button className="font-semibold truncate hover:text-[hsl(var(--primary))] block text-left" onClick={() => navigate(`/app/arcade/${s.game_id}`)}>{s.title}</button>
                          <div className="text-xs text-[hsl(var(--muted-foreground))]">by {s.user_name}{s.mine ? " · you" : ""}</div>
                        </div>
                        <span className="font-bold text-sm">{s.votes}</span>
                        <Button size="sm" variant={detail.my_vote_submission_id === s.id ? "default" : "outline"} disabled={s.mine}
                          className="rounded-full shrink-0" onClick={() => vote(s.id)} data-testid={`arena-vote-${s.id}`}>
                          <Heart className={`w-4 h-4 ${detail.my_vote_submission_id === s.id ? "fill-current" : ""}`} />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
